<template>
    <v-form ref="form">
        <v-row>
            <v-col cols="12" md="5">
                <v-text-field
                    outlined
                    v-model="cnpj"
                    :rules="cnpjRules"
                    label="CNPJ do Hotel"
                    v-mask="'NN.NNN.NNN/NNNN-##'"
                    required
                    :hide-details="hideDetailsOnFormFields"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="7">
                <v-text-field
                    outlined
                    v-model="hotelName"
                    :rules="hotelNameRules"
                    label="Nome do Hotel"
                    required
                    :hide-details="hideDetailsOnFormFields"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="5">
                <v-text-field
                    outlined
                    v-model="ownerPhone"
                    v-mask="getOwnerPhoneMask"
                    :rules="ownerPhoneRules"
                    label="Telefone do Responsável"
                    required
                    :hide-details="hideDetailsOnFormFields"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="7">
                <v-text-field
                    outlined
                    v-model="ownerEmail"
                    :rules="ownerEmailRules"
                    label="Email do Responsável"
                    required
                    :hide-details="hideDetailsOnFormFields"
                ></v-text-field>
            </v-col>
            <v-col cols="12" class="mt-2">
                <v-container class="payment-container">
                    <span class="d-block mb-4">Parceiro:</span>
                    <v-radio-group
                        v-model="partner"
                        row
                        class="ma-0 pa-0"
                        :rules="partnerRules"
                    >
                        <v-radio label="Omnibees" value="omnibees"></v-radio>
                        <v-radio label="Amadeus" value="amadeus"></v-radio>
                        <v-radio label="Nenhum" value="none"></v-radio>
                    </v-radio-group>
                </v-container>
            </v-col>
            <v-col cols="12" md="6" offset-md="3" class="pt-4 pt-sm-12 pb-8 pb-sm-2">
                <v-btn
                    depressed
                    color="success"
                    class="w-100"
                    @click="validateAndFinish"
                >
                    Finalizar
                </v-btn>
            </v-col>
        </v-row>
        <response-dialog
            :model="hasCnpjValidationError"
            :message="cnpjValidationsError"
            type="error"
            @close="closeCnpjValidationErrorModal"
        />
        <rounded-dialog
            :model="pendingPreRegistrationFoundDialog"
            title="Pré Cadastro Encontrado!"
            icon-type="info"
        >
            <template v-slot:body>
                <div class="text-h6 font-weight-medium mb-2">Você já realizou o pré-cadastro...</div>
                <p class="text-body-2">
                    Siga para o cadastro ou copie o link abaixo para finalizar depois:
                </p>
                <v-chip
                    class="mt-4 mb-2 text-wrap py-10 link-chip"
                    color="blue lighten-5"
                    text-color="primary"
                    outlined
                    label
                >
                    {{ ownerRegistrationLink }}
                </v-chip>
            </template>
            <template v-slot:actions>
                <v-btn text color="grey darken-1" @click="closePendingPreRegistrationFoundDialog">Fechar</v-btn>
                <v-btn color="primary" depressed @click="redirectToHotelCreationForm">
                    Ir para o cadastro
                </v-btn>
            </template>
        </rounded-dialog>
        <rounded-dialog
            :model="preRegistrationSuccessDialog"
            title="Pré Cadastro Efetivado!"
        >
            <template v-slot:body>
                <v-icon size="72" color="primary" class="mb-2">mdi-office-building</v-icon>
                <div class="text-h6 font-weight-medium mb-2">Estamos quase lá!</div>
                <p class="grey--text text--darken-1 mt-2 mb-4">
                    Só precisamos de mais algumas informações sobre o estabelecimento...
                </p>
                <p class="text-body-2">
                    Você pode continuar agora ou copiar o link abaixo para finalizar depois:
                </p>
                <v-chip
                    class="mt-4 mb-2 text-wrap py-10 link-chip"
                    color="blue lighten-5"
                    text-color="primary"
                    outlined
                    label
                >
                    {{ ownerRegistrationLink }}
                </v-chip>
            </template>
            <template v-slot:actions>
                <v-btn text color="grey darken-1" @click="closePreRegistrationSuccessDialog">Fechar</v-btn>
                <v-btn color="primary" depressed @click="redirectToHotelCreationForm">
                    Ir para o cadastro
                </v-btn>
            </template>
        </rounded-dialog>
        <rounded-dialog
            :model="partnerClosingDialog"
            title="Recebemos suas informações!"
            icon-type="info"
        >
            <template v-slot:body>
                <p class="text-body-2">
                    Obrigado pelo seu interesse em ser parceiro da Onfly! Em breve, entraremos em contato.
                </p>
            </template>
            <template v-slot:actions>
                <v-btn text color="grey darken-1" @click="closePartnerClosingDialog">Fechar</v-btn>
            </template>
        </rounded-dialog>
    </v-form>
</template>

<script>

import { isValidCnpj } from "../../../helpers/ValidationRuleHelper";
import ResponseDialog from "../../Dialog/ResponseDialog.vue";
import RoundedDialog from "../../Dialog/RoundedDialog.vue";

export default {
    name: "PreRegistration",
    components: { RoundedDialog, ResponseDialog },
    data: () => ({
        cnpj: '',
        cnpjRules: [
            v => !!v || 'O CNPJ é obrigatório',
            v => isValidCnpj(v) || 'O CNPJ é inválido',
        ],
        cnpjHasError: false,
        awaitingCnpjValidation: false,
        cnpjValidationsError: '',
        hotelName: '',
        hotelNameRules: [
            v => !!v || 'O nome é obrigatório'
        ],
        ownerPhone: '',
        ownerPhoneRules: [
            v => !!v || 'O telefone do responsável é obrigatório',
            v => !v || !!v.match('^\\([0-9]{2}\\)\\ [0-9]{4}\\-[0-9]{4}$') ||
                !!v.match('^\\([0-9]{2}\\)\\ [0-9]{5}\\-[0-9]{4}$') ||
                `Informe um telefone valido`
        ],
        ownerEmail: '',
        ownerEmailRules: [
            v => !!v || 'O email é obrigatório',
            v => !!v.match('^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$') || 'O email é inválido'
        ],
        partner: 'none',
        partnerRules: [
            v => !!v || 'É obrigatório selecionar uma opção de parceiro',
        ],
        ownerPreRegistrationHash: null,
        preRegistrationSuccessDialog: false,
        pendingPreRegistrationFoundDialog: false,
        partnerClosingDialog: false
    }),
    methods: {
        async validateIfHotelHasPendingPreRegistration() {
            if (this.isCnpjFullyFilled) {
                this.awaitingCnpjValidation = true;
                await this.makePendingRegistrationValidation();
                this.awaitingCnpjValidation = false;
            }
        },
        async makePendingRegistrationValidation () {
            try {
                const response = await axios.get(`/api/v1/owner-creation/get-pending-registration`,
                    { params: { cnpj: this.getOnlyAplhaNumericChars(this.cnpj) }, validateStatus: () => true }
                );

                if (response.data?.hasPendingRegistration) {
                    this.handlePendingRegistrationFound(response);
                }
            } catch (error) {
                this.cnpjValidationsError = error.message;
            }
        },
        handlePendingRegistrationFound (response) {
            const foundPartner = response.data?.hotelPreRegistration?.partner;
            if (foundPartner && foundPartner !== 'none') {
                this.openPartnerClosingDialog();
                return;
            }
            this.openPendingPreRegistrationFoundDialog(response);
        },
        getOnlyAplhaNumericChars (value) {
            return value.replace(/[.\/-]/g, '').toUpperCase();
        },
        throwErrorOnValidationFail (response) {
            const errorMessage = response.data?.message ?? 'Ocorreu um erro inesperado';
            throw new Error(errorMessage);
        },
        closeCnpjValidationErrorModal () {
            this.cnpjValidationsError = '';
        },
        getOwnerPhoneMask (value) {
            let numbers = value.match(/\d/g);
            let numberLength = 0;
            if (numbers) {
                numberLength = numbers.join("").length;
            }

            if (numberLength > 10) {
                return ['(', /[1-9]/, /[1-9]/, ')', ' ', /\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
            } else {
                return ['(', /[1-9]/, /[1-9]/, ')', ' ', /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
            }
        },
        validateAndFinish () {
            if (!this.$refs.form.validate()) { return; }
            axios.post(`/api/v1/owner-creation/pre-registration`, this.getFormData(), { validateStatus: () => true })
                .then(response => {
                    const isValidated = !!response.data?.success && !!response.data?.data?.hash;
                    isValidated
                        ? this.onPreRegistrationSuccess(response.data.data.hash)
                        : this.throwErrorOnValidationFail(response)

                });
        },
        getFormData () {
            return {
              cnpj: this.getOnlyAplhaNumericChars(this.cnpj),
              name: this.hotelName,
              phone: this.ownerPhone,
              email: this.ownerEmail,
              partner: this.partner
            };
        },
        onPreRegistrationSuccess (hash) {
            this.ownerPreRegistrationHash = hash;
            if (this.partner === 'none') {
                this.openRegistrationSuccessDialog();
                return;
            }
            this.openPartnerClosingDialog();
        },
        openRegistrationSuccessDialog () {
            this.preRegistrationSuccessDialog = true;
        },
        closePreRegistrationSuccessDialog () {
            this.preRegistrationSuccessDialog = false;
        },
        redirectToHotelCreationForm () {
            this.$router.push(this.ownerRegistrationRoute)
        },
        openPendingPreRegistrationFoundDialog (response) {
            this.pendingPreRegistrationFoundDialog = true;
            this.ownerPreRegistrationHash = response.data.hotelPreRegistration.hash;
        },
        closePendingPreRegistrationFoundDialog () {
            this.pendingPreRegistrationFoundDialog = false;
        },
        openPartnerClosingDialog () {
            this.partnerClosingDialog = true;
        },
        closePartnerClosingDialog () {
            this.partnerClosingDialog = false;
        }
    },
    watch: {
        cnpj (val) {
            if (val && val !== val.toUpperCase()) {
                this.cnpj = val.toUpperCase();
                return;
            }
            this.validateIfHotelHasPendingPreRegistration();
        }
    },
    computed: {
        isCnpjFullyFilled () {
            if (this.cnpj === null) {
                return false;
            }

            return this.getOnlyAplhaNumericChars(this.cnpj).length === 14;
        },
        hasCnpjValidationError () {
            return this.cnpjValidationsError !== null && this.cnpjValidationsError !== '';
        },
        ownerRegistrationRoute () {
            return `/new-hotel/${this.ownerPreRegistrationHash}?source=initial-form`;
        },
        ownerRegistrationLink () {
            return `${process.env.MIX_PARTNER_HUB_HOST}${this.ownerRegistrationRoute}`;
        },
        hideDetailsOnFormFields () {
            return 'auto';
        }
    },
    created() {
    },
}
</script>

<style scoped>

.link-chip {
    white-space: normal; word-break: break-all;
}

.payment-container {
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, .38);
    padding: 20px;
}

</style>
